import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaSearch, 
  FaFilter, 
  FaGlobeAmericas,
  FaGlobeAsia,
  FaGlobeEurope,
  FaGlobeAfrica,
  FaMapMarkerAlt,
  FaClock,
  FaDollarSign,
  FaCalendarAlt,
  FaFlag,
  FaPlane,
  FaUsers,
  FaUniversity,
  FaHeart,
  FaCheckCircle
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const CountriesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedVisaType, setSelectedVisaType] = useState('all');

  const regions = [
    { id: 'all', name: 'All Regions', icon: <FaGlobeAmericas />, count: 50 },
    { id: 'europe', name: 'Europe', icon: <FaGlobeEurope />, count: 26 },
    { id: 'north-america', name: 'North America', icon: <FaGlobeAmericas />, count: 3 },
    { id: 'asia', name: 'Asia', icon: <FaGlobeAsia />, count: 15 },
    { id: 'oceania', name: 'Oceania', icon: <FaGlobeAmericas />, count: 2 },
    { id: 'africa', name: 'Africa', icon: <FaGlobeAfrica />, count: 4 },
  ];

  const visaTypes = [
    { id: 'all', name: 'All Visa Types' },
    { id: 'tourist', name: 'Tourist Visa' },
    { id: 'student', name: 'Student Visa' },
    { id: 'business', name: 'Business Visa' },
    { id: 'work', name: 'Work Visa' },
    { id: 'family', name: 'Family Visa' },
  ];

  const countries = [
    {
      id: 1,
      name: 'United States',
      flag: '🇺🇸',
      region: 'north-america',
      capital: 'Washington D.C.',
      popularFor: ['Education', 'Business', 'Tourism'],
      visaTypes: ['tourist', 'student', 'business', 'work', 'family'],
      processingTime: '2-4 weeks',
      successRate: '92%',
      difficulty: 'Medium',
      description: 'World leader in education and business opportunities',
      icon: <FaUniversity />
    },
    {
      id: 2,
      name: 'United Kingdom',
      flag: '🇬🇧',
      region: 'europe',
      capital: 'London',
      popularFor: ['Education', 'History', 'Business'],
      visaTypes: ['tourist', 'student', 'business', 'work'],
      processingTime: '3-5 weeks',
      successRate: '94%',
      difficulty: 'Medium',
      description: 'Prestigious universities and rich cultural heritage',
      icon: <FaUniversity />
    },
    {
      id: 3,
      name: 'Canada',
      flag: '🇨🇦',
      region: 'north-america',
      capital: 'Ottawa',
      popularFor: ['Immigration', 'Education', 'Quality of Life'],
      visaTypes: ['tourist', 'student', 'work', 'family'],
      processingTime: '4-8 weeks',
      successRate: '95%',
      difficulty: 'Medium',
      description: 'Top destination for skilled immigrants and students',
      icon: <FaUsers />
    },
    {
      id: 4,
      name: 'Australia',
      flag: '🇦🇺',
      region: 'oceania',
      capital: 'Canberra',
      popularFor: ['Work', 'Study', 'Beaches'],
      visaTypes: ['tourist', 'student', 'work'],
      processingTime: '4-6 weeks',
      successRate: '93%',
      difficulty: 'Medium',
      description: 'Excellent work opportunities and world-class education',
      icon: <FaPlane />
    },
    {
      id: 5,
      name: 'Germany',
      flag: '🇩🇪',
      region: 'europe',
      capital: 'Berlin',
      popularFor: ['Education', 'Engineering', 'Schengen'],
      visaTypes: ['tourist', 'student', 'business', 'work'],
      processingTime: '2-3 weeks',
      successRate: '96%',
      difficulty: 'Easy',
      description: 'Free education and strong engineering programs',
      icon: <FaUniversity />
    },
    {
      id: 6,
      name: 'France',
      flag: '🇫🇷',
      region: 'europe',
      capital: 'Paris',
      popularFor: ['Tourism', 'Culture', 'Schengen'],
      visaTypes: ['tourist', 'student'],
      processingTime: '10-15 days',
      successRate: '95%',
      difficulty: 'Easy',
      description: 'Cultural capital of Europe with rich history',
      icon: <FaHeart />
    },
    {
      id: 7,
      name: 'Japan',
      flag: '🇯🇵',
      region: 'asia',
      capital: 'Tokyo',
      popularFor: ['Technology', 'Culture', 'Business'],
      visaTypes: ['tourist', 'student', 'business'],
      processingTime: '5-10 days',
      successRate: '98%',
      difficulty: 'Easy',
      description: 'Advanced technology and unique cultural experiences',
      icon: <FaPlane />
    },
    {
      id: 8,
      name: 'Singapore',
      flag: '🇸🇬',
      region: 'asia',
      capital: 'Singapore',
      popularFor: ['Business', 'Education', 'Technology'],
      visaTypes: ['tourist', 'student', 'business', 'work'],
      processingTime: '3-7 days',
      successRate: '99%',
      difficulty: 'Easy',
      description: 'Global business hub with excellent education',
      icon: <FaUsers />
    },
    {
      id: 9,
      name: 'Spain',
      flag: '🇪🇸',
      region: 'europe',
      capital: 'Madrid',
      popularFor: ['Tourism', 'Language', 'Schengen'],
      visaTypes: ['tourist', 'student'],
      processingTime: '10-20 days',
      successRate: '94%',
      difficulty: 'Easy',
      description: 'Beautiful beaches and rich cultural heritage',
      icon: <FaHeart />
    },
    {
      id: 10,
      name: 'New Zealand',
      flag: '🇳🇿',
      region: 'oceania',
      capital: 'Wellington',
      popularFor: ['Nature', 'Work', 'Study'],
      visaTypes: ['tourist', 'student', 'work'],
      processingTime: '3-6 weeks',
      successRate: '92%',
      difficulty: 'Medium',
      description: 'Stunning natural beauty and friendly immigration policies',
      icon: <FaPlane />
    },
    {
      id: 11,
      name: 'Italy',
      flag: '🇮🇹',
      region: 'europe',
      capital: 'Rome',
      popularFor: ['Culture', 'Food', 'Schengen'],
      visaTypes: ['tourist', 'student'],
      processingTime: '10-15 days',
      successRate: '95%',
      difficulty: 'Easy',
      description: 'Historical landmarks and world-renowned cuisine',
      icon: <FaHeart />
    },
    {
      id: 12,
      name: 'United Arab Emirates',
      flag: '🇦🇪',
      region: 'asia',
      capital: 'Abu Dhabi',
      popularFor: ['Business', 'Luxury', 'Work'],
      visaTypes: ['tourist', 'business', 'work'],
      processingTime: '3-5 days',
      successRate: '98%',
      difficulty: 'Easy',
      description: 'Modern business hub with tax-free income',
      icon: <FaUsers />
    }
  ];

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         country.capital.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === 'all' || country.region === selectedRegion;
    const matchesVisaType = selectedVisaType === 'all' || country.visaTypes.includes(selectedVisaType);
    
    return matchesSearch && matchesRegion && matchesVisaType;
  });

  const stats = {
    totalCountries: countries.length,
    averageProcessing: '3-4 weeks',
    overallSuccess: '95%',
    popularDestination: 'United States'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary-500 rounded-full blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-primary-400 rounded-full blur-3xl opacity-20"></div>
        </div>
        
        <div className="relative container-padding py-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-bold mb-6"
            >
              Explore Visa Destinations
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-primary-100 mb-8"
            >
              Discover visa requirements, processing times, and opportunities in 50+ countries worldwide
            </motion.p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="bg-white shadow-lg">
        <div className="container-padding py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Countries', value: stats.totalCountries, icon: <FaGlobeAmericas /> },
              { label: 'Avg Processing', value: stats.averageProcessing, icon: <FaClock /> },
              { label: 'Success Rate', value: stats.overallSuccess, icon: <FaCheckCircle /> },
              { label: 'Most Popular', value: stats.popularDestination, icon: <FaFlag /> }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full text-primary-600 mb-3">
                  {stat.icon}
                </div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <section className="py-12">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            {/* Search Bar */}
            <div className="relative mb-8">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search countries or capitals..."
                className="w-full pl-12 pr-4 py-4 bg-white rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-6 mb-8">
              {/* Region Filter */}
              <div className="flex-1">
                <h3 className="font-bold text-gray-700 mb-3 flex items-center">
                  <FaFilter className="mr-2" /> Region
                </h3>
                <div className="flex flex-wrap gap-2">
                  {regions.map(region => (
                    <button
                      key={region.id}
                      onClick={() => setSelectedRegion(region.id)}
                      className={`flex items-center px-4 py-2 rounded-full transition ${
                        selectedRegion === region.id
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span className="mr-2">{region.icon}</span>
                      {region.name}
                      <span className="ml-2 text-sm opacity-75">({region.count})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Visa Type Filter */}
              <div className="flex-1">
                <h3 className="font-bold text-gray-700 mb-3">Visa Type</h3>
                <div className="flex flex-wrap gap-2">
                  {visaTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedVisaType(type.id)}
                      className={`px-4 py-2 rounded-full transition ${
                        selectedVisaType === type.id
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Count */}
            <div className="flex justify-between items-center mb-6">
              <div className="text-gray-700">
                Showing <span className="font-bold">{filteredCountries.length}</span> of {countries.length} countries
              </div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRegion('all');
                  setSelectedVisaType('all');
                }}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear all filters
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Grid */}
      <section className="pb-20">
        <div className="container-padding">
          {filteredCountries.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🌎</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No countries found</h3>
              <p className="text-gray-600">Try adjusting your search criteria</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCountries.map((country, index) => (
                <motion.div
                  key={country.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
                >
                  {/* Country Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <span className="text-3xl mr-3">{country.flag}</span>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{country.name}</h3>
                          <div className="flex items-center text-gray-600 text-sm">
                            <FaMapMarkerAlt className="mr-1" />
                            {country.capital}
                          </div>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(country.difficulty)}`}>
                        {country.difficulty}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4">{country.description}</p>
                    
                    <div className="flex flex-wrap gap-2">
                      {country.popularFor.map(tag => (
                        <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Visa Information */}
                  <div className="p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Visa Information</h4>
                    
                    <div className="space-y-4">
                      {/* Processing Time */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-gray-700">
                          <FaClock className="mr-3 text-primary-600" />
                          <span>Processing Time</span>
                        </div>
                        <span className="font-medium">{country.processingTime}</span>
                      </div>
                      
                      {/* Success Rate */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-gray-700">
                          <FaCheckCircle className="mr-3 text-green-600" />
                          <span>Success Rate</span>
                        </div>
                        <span className="font-medium text-green-600">{country.successRate}</span>
                      </div>
                      
                      {/* Available Visas */}
                      <div>
                        <div className="text-gray-700 mb-2">Available Visa Types:</div>
                        <div className="flex flex-wrap gap-2">
                          {country.visaTypes.map(type => (
                            <span key={type} className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm">
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="mt-6 flex gap-3">
                      <Link
                        to={`/services?country=${country.name.toLowerCase()}`}
                        className="flex-1 btn-primary text-center py-2"
                      >
                        View Services
                      </Link>
                      <Link
                        to={`/requirements?country=${country.name.toLowerCase()}`}
                        className="flex-1 btn-secondary text-center py-2"
                      >
                        Requirements
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Regional Guides */}
      <section className="py-20 bg-white">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Regional Visa Guides
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized information for different regions of the world
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                region: 'Europe',
                description: 'Schengen Area and European Union countries',
                countries: 26,
                highlight: 'Single visa for 26 countries',
                color: 'from-blue-500 to-blue-700'
              },
              {
                region: 'North America',
                description: 'USA, Canada, and business hubs',
                countries: 3,
                highlight: 'Top education destinations',
                color: 'from-red-500 to-red-700'
              },
              {
                region: 'Asia',
                description: 'Fast-growing economies and cultural hubs',
                countries: 15,
                highlight: 'Quick processing times',
                color: 'from-green-500 to-green-700'
              },
              {
                region: 'Oceania',
                description: 'Australia, New Zealand, and island nations',
                countries: 2,
                highlight: 'Work opportunities',
                color: 'from-purple-500 to-purple-700'
              }
            ].map((guide, index) => (
              <motion.div
                key={guide.region}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-gradient-to-br ${guide.color} text-white rounded-2xl p-6`}
              >
                <h3 className="text-2xl font-bold mb-3">{guide.region}</h3>
                <p className="text-white/90 mb-4">{guide.description}</p>
                <div className="flex items-center justify-between mt-6">
                  <div>
                    <div className="text-3xl font-bold">{guide.countries}</div>
                    <div className="text-sm opacity-90">Countries</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{guide.highlight}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900 to-primary-700 text-white">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Need Help Choosing a Country?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-primary-100">
              Our experts can help you find the perfect destination based on your goals and qualifications
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary bg-white text-primary-700 hover:bg-primary-100 px-8 py-4 text-lg"
              >
                Get Personalized Advice
              </Link>
              <Link
                to="/process"
                className="btn-secondary border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                View Application Process
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CountriesPage;